import { ReactNode } from 'react';

import { styled } from '../../../stitches.config';
import { ORIENTATION_LEFT, ORIENTATION_RIGHT } from './utils/const';

export { ORIENTATION_LEFT, ORIENTATION_RIGHT };

interface SignProps {
  size: number;
}

const PRIMARY_COLOR = '#738694';

const Sign = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignitems: 'center',
});

const ArrowBottomContext = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: PRIMARY_COLOR,
});

const ArrowTopContext = styled(ArrowBottomContext, {
  justifyContent: 'flex-end',
});

const ArrowHead = styled('path', {
  fill: PRIMARY_COLOR,
});

export interface ArrowProps {
  lengthScale?: number;
  topContext?: ReactNode;
  bottomContext?: ReactNode;
}

interface ArrowSignProps extends ArrowProps, SignProps {
  orientation?: string;
}

export function ArrowSignSVG(props: ArrowSignProps) {
  const { orientation = ORIENTATION_LEFT, lengthScale = 1 } = props;
  const coordsModifier = orientation === ORIENTATION_LEFT ? 0 : -15;

  return (
    <svg viewBox={`0 0 ${lengthScale * 100} ${100 / 3}`} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <marker
          id="arrowhead"
          viewBox="0 0 20 10"
          markerWidth="10"
          markerHeight="10"
          refX="5"
          refY="5"
          markerUnits="strokeWidth"
          orient={orientation === ORIENTATION_LEFT ? 'auto-start-reverse' : ''}
        >
          <ArrowHead
            d={orientation === ORIENTATION_LEFT ? 'M 20 0 L 0 5 L 20 10 L 17 5 z' : 'M 0 0 L 20 5 L 0 10 L 3 5 z'}
            fill={PRIMARY_COLOR}
          />
        </marker>
      </defs>

      <line
        x1={15 + coordsModifier}
        y1={100 / 6}
        x2={lengthScale * 100 + coordsModifier}
        y2={100 / 6}
        markerStart={orientation === ORIENTATION_LEFT ? 'url(#arrowhead)' : ''}
        markerEnd={orientation === ORIENTATION_RIGHT ? 'url(#arrowhead)' : ''}
        stroke={PRIMARY_COLOR}
        strokeWidth={2}
      />
    </svg>
  );
}

export function ArrowSign(props: ArrowSignProps) {
  const { size, lengthScale = 1, topContext, bottomContext } = props;

  const signWidth = (lengthScale * size) / 3;
  const signHeight = size;
  const signMargin = size / 16;
  const svgHeight = size * 0.1;
  const contextHeight = size * 0.45;

  return (
    <Sign
      css={{
        'width': `${signWidth}rem`,
        'height': `${signHeight}rem`,
        'margin': `0 ${signMargin}rem`,
        '& svg': {
          height: `${svgHeight}rem`,
        },
      }}
    >
      <ArrowTopContext css={{ height: `${contextHeight}rem` }}>{topContext}</ArrowTopContext>

      <ArrowSignSVG {...props} />

      <ArrowBottomContext css={{ height: `${contextHeight}rem` }}>{bottomContext}</ArrowBottomContext>
    </Sign>
  );
}

export function PlusSignSVG() {
  return (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <line x1={40} y1={50} x2={60} y2={50} stroke={PRIMARY_COLOR} strokeWidth={2} />
      <line x1={50} y1={40} x2={50} y2={60} stroke={PRIMARY_COLOR} strokeWidth={2} />
    </svg>
  );
}

export function PlusSign(props: SignProps) {
  const { size } = props;

  const signWidth = size / 3;
  const signHeight = size;
  const signMargin = size / 16;
  const svgHeight = size / 3;

  return (
    <Sign
      css={{
        'width': `${signWidth}rem`,
        'height': `${signHeight}rem`,
        'margin': `0 ${signMargin}rem`,
        '& svg': {
          height: `${svgHeight}rem`,
        },
      }}
    >
      <PlusSignSVG />
    </Sign>
  );
}
